// LoanBalanceReport.jsx
import * as React from "react";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  alpha,
  Fade,
  Card
} from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import { DataGrid } from "@mui/x-data-grid";
import AppNavbar from "../../../../components/AppNavbar";
import Header from "../../../../components/Header";
import SideMenu from "../../../../components/SideMenu";
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../../../dashboard/theme/customizations';
import AppTheme from "../../../../shared-theme/AppTheme";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import CustomDatePicker from "../../../../components/CustomDatePicker";
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { formatNumberWithCommas } from "../../../../utils/generalFunctions";

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const MetricCard = ({ label, value, icon, delay = 0 }) => (
    <Fade in timeout={800 + delay}>
    <Card sx={{ 
      p: 3, 
      minWidth: 280, 
      flex: 1,
        background: 'linear-gradient(135deg, #18586b 0%, #0f3a42 100%)',
      color: 'white',
      position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: '0 20px 40px rgba(24, 88, 107, 0.3)',
          '& .metric-icon': {
            transform: 'scale(1.1) rotate(5deg)',
          },
          '& .metric-value': {
            transform: 'scale(1.05)',
          }
        }
    }}>
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ opacity: 0.9 }}>
            {label}
          </Typography>
          {icon && (
              <Box className="metric-icon" sx={{ 
                p: 1.5, 
              borderRadius: '50%', 
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.1)'
            }}>
              {icon}
            </Box>
          )}
        </Box>
          <Typography className="metric-value" variant="h4" fontWeight={800} sx={{ 
            mb: 1,
            transition: 'all 0.3s ease',
            background: 'linear-gradient(45deg, #ffffff, #e0f2f1)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
          {value}
        </Typography>
          
        
          
        
      </Box>
        
        {/* Animated background elements */}
      <Box sx={{
        position: 'absolute',
        top: -20,
        right: -20,
        width: 80,
        height: 80,
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.1)',
          zIndex: 1,
          animation: 'float 6s ease-in-out infinite'
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: -30,
          left: -30,
          width: 60,
          height: 60,
          borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.05)',
          zIndex: 1,
          animation: 'float 4s ease-in-out infinite reverse'
        }} />
        
        <style jsx>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-10px) rotate(180deg); }
          }
        `}</style>
    </Card>
    </Fade>
  );

export default function BalanceDePrestamosReport(props) {
  const navigate = useNavigate();
  // ---- Filters (controlled state) ----
  const [branch, setBranch] = React.useState("");
  const [product, setProduct] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [dateFrom, setDateFrom] = React.useState(null);
  const [dateTo, setDateTo] = React.useState(null);

  // ---- Data state ----
  const [rows, setRows] = React.useState([]);
  const [filteredRows, setFilteredRows] = React.useState([]);
  const [isLoadingData, setIsLoadingData] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  // ---- Extract unique values for filter options ----
  const filterOptions = React.useMemo(() => {
    if (rows.length === 0) {
      return {
        branches: [],
        products: [],
        statuses: []
      };
    }

    // Extract unique branches
    const branches = [...new Set(rows.map(row => row.branch).filter(Boolean))].sort();

    // Extract unique products
    const products = [...new Set(rows.map(row => row.product).filter(Boolean))].sort();

    // Extract unique statuses
    const statuses = [...new Set(rows.map(row => row.status).filter(Boolean))].sort();

    return {
      branches,
      products,
      statuses
    };
  }, [rows]);

  const currency = (n) => {
    if (n === undefined || n === null || isNaN(n)) return 'RD$0';
    return `RD$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  const columns = [
    { field: "loanId", headerName: "ID Préstamo", flex: 1, minWidth: 150 },
    { field: "client", headerName: "Cliente", flex: 1, minWidth: 200 },
    { field: "product", headerName: "Producto", flex: 1, minWidth: 180 },
    { field: "branch", headerName: "Sucursal", flex: 1, minWidth: 150 },
    {
      field: "approvedAmount",
      headerName: "Monto Aprobado",
      minWidth: 160,
      renderCell: (params) => {
        return <Typography variant="body2" mt={2}>{formatNumberWithCommas(params.row.approvedAmount.toFixed(2))}</Typography>
      }
    },
    {
      field: "interestPaid",
      headerName: "Interes Pagado",
      minWidth: 140,
      renderCell: (params) => {
        return <Typography variant="body2" mt={2}>{formatNumberWithCommas(params.row.interestPaid.toFixed(2))}</Typography>
      }
    },
    {
      field: "capitalPaid",
      headerName: "Capital Pagado",
      minWidth: 140,
      renderCell: (params) => {
        return <Typography variant="body2" mt={2}>{formatNumberWithCommas(params.row.capitalPaid.toFixed(2))}</Typography>
      }
    },
    {
      field: "balanceCapital",
      headerName: "Capital Pendiente",
      minWidth: 140,
      renderCell: (params) => {
        return <Typography variant="body2" mt={2}>{formatNumberWithCommas(params.row.balanceCapital.toFixed(2))}</Typography>
      }
    },
   
   
  ];

  // ---- Simple totals (footer-like summary) ----
  const totals = filteredRows.reduce(
    (acc, r) => {
      acc.totalApproved += parseFloat(r.approvedAmount) || 0;
      acc.totalCapitalPaid += parseFloat(r.capitalPaid) || 0;
      acc.totalInterestPaid += parseFloat(r.interestPaid) || 0;
      acc.completedLoans += r.status === 'saldado' ? 1 : 0;
      return acc;
    },
    { totalApproved: 0, totalCapitalPaid: 0, totalInterestPaid: 0, activeLoans: filteredRows.length, completedLoans: 0 }
  );

  // ---- Fetch data from API ----
  const fetchData = async () => {
    setIsLoadingData(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/prestamos/reporte/balancedeprestamos?idcompany=${localStorage.getItem('idcompany')}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!Array.isArray(data)) {
        console.error('API response is not an array:', data);
        return;
      }

      // Transform API data to match frontend expectations
      const transformedData = data.map((item, index) => ({
        id: index + 1, // Generate sequential ID
        loanId: item.loan_id || `PREST-${item.id_prestamo || index}`,
        client: item.client || 'Cliente no especificado',
        product: item.product || 'Producto no especificado',
        branch: item.branch || 'Sucursal no especificada',
        approvedAmount: parseFloat(item.approvedAmount) || 0,
        interestPaid: parseFloat(item.interestPaid) || 0,
        capitalPaid: parseFloat(item.capitalPaid) || 0,
        balanceCapital: parseFloat(item.balanceCapital) || 0,
        balanceInterest: parseFloat(item.balanceInterest) || 0,
        startDate: item.startDate || new Date().toISOString().split('T')[0],
        nextPayment: item.nextPayment || null
      }));

      setRows(transformedData);
      setFilteredRows(transformedData); // Initialize filtered data
      console.log('Data loaded successfully:', transformedData);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      alert(`Error al cargar los datos: ${error.message}`);
    } finally {
      setIsLoadingData(false);
    }
  };

  // ---- Filter data based on selected filters ----
  const handleGenerate = () => {
    setIsLoading(true);
    
    // Simulate processing time
    setTimeout(() => {
      let filtered = [...rows];

      // Filter by branch
      if (branch) {
        filtered = filtered.filter(row => row.branch === branch);
      }

      // Filter by product
      if (product) {
        filtered = filtered.filter(row => row.product === product);
      }

      // Filter by status
      if (status) {
        filtered = filtered.filter(row => row.status === status);
      }

      // Filter by date range
      if (dateFrom) {
        filtered = filtered.filter(row => {
          const rowDate = dayjs(row.startDate.split('/').reverse().join('-'));
          return rowDate.isAfter(dateFrom) || rowDate.isSame(dateFrom, 'day');
        });
      }
      if (dateTo) {
        filtered = filtered.filter(row => {
          const rowDate = dayjs(row.startDate.split('/').reverse().join('-'));
          return rowDate.isBefore(dateTo) || rowDate.isSame(dateTo, 'day');
        });
      }

      setFilteredRows(filtered);
      setIsLoading(false);
      console.log('Data filtered successfully:', filtered);
    }, 500);
  };

  const handleClearFilters = () => {
    setBranch("");
    setProduct("");
    setStatus("");
    setDateFrom(null);
    setDateTo(null);
    setFilteredRows(rows); // Reset to show all data
  };

  const handleExportPDF = () => {
    // TODO: implement server-side or client-side PDF export
    console.log("Export PDF");
  };
  const handleExportExcel = () => {
    // TODO: implement Excel export
    console.log("Export Excel");
  };
  const handlePrint = () => window.print();

  // ---- Filter content ----
  const filterContent = (
    <Box>
      {/* Filter Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1 }}>
              Sucursal
            </Typography>
            <FormControl fullWidth>
              <Select
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                displayEmpty
                sx={{ backgroundColor: 'white' }}
              >
                <MenuItem value="">Todas las Sucursales</MenuItem>
                {filterOptions.branches.map((branchOption) => (
                  <MenuItem key={branchOption} value={branchOption}>
                    {branchOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1 }}>
              Producto
            </Typography>
            <FormControl fullWidth>
              <Select
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                displayEmpty
                sx={{ backgroundColor: 'white' }}
              >
                <MenuItem value="">Todos los Productos</MenuItem>
                {filterOptions.products.map((productOption) => (
                  <MenuItem key={productOption} value={productOption}>
                    {productOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>


        <Grid item xs={12} sm={6} md={3}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1 }}>
              Fecha Desde
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dateFrom}
                format="DD/MM/YYYY"
                onChange={(newValue) => setDateFrom(newValue)}
                label="Seleccionar fecha"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { backgroundColor: 'white' }
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left', mb: 1 }}>
              Fecha Hasta
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dateTo}
                format="DD/MM/YYYY"
                onChange={(newValue) => setDateTo(newValue)}
                label="Seleccionar fecha"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { backgroundColor: 'white' }
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Button 
            variant="contained" 
            onClick={handleGenerate} 
            color="primarybutton" 
            disabled={isLoading}
            sx={{fontWeight: 600, minWidth: 120, mt: 3}}
            fullWidth
          >
            {isLoading ? 'Filtrando...' : 'Aplicar Filtros'}
          </Button>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Button 
            variant="outlined" 
            onClick={handleClearFilters} 
            disabled={isLoading}
            sx={{fontWeight: 600, minWidth: 120, mt: 3}}
            fullWidth
          >
            Limpiar Filtros
          </Button>
        </Grid>
      </Grid>

      {/* Export Options Row */}
      <Stack 
        direction={{ xs: "column", sm: "row" }} 
        spacing={2} 
        alignItems="center"
        sx={{ 
          borderTop: 1, 
          borderColor: 'divider', 
          pt: 2,
          justifyContent: 'space-between'
        }}
      >
        <Stack direction="row" spacing={1}>
          <Button onClick={handleExportPDF} startIcon={<PictureAsPdfOutlinedIcon />} color="inherit" size="small">
            Exportar a PDF
          </Button>
          <Button onClick={handleExportExcel} startIcon={<GridOnOutlinedIcon />} color="inherit" size="small">
            Exportar a Excel
          </Button>
          <Button onClick={handlePrint} startIcon={<PrintOutlinedIcon />} color="inherit" size="small">
            Imprimir Reporte
          </Button>
        </Stack>
      </Stack>
    </Box>
  );

  React.useEffect(() => {
    fetchData();
  }, []);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        {/* Main content */}
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}
          >
            <Header parent={"Gestión Comercial"} child={"Reportes"} />
            <Container maxWidth={false} sx={{ px: 5, py: 5 }}>
              <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', fontFamily: 'Inter, "Noto Sans", sans-serif' }}>
                <Box maxWidth="lg" mx="auto" py={4}>
                  <Box display="flex" px={2} mb={2}>
                    <Button variant="outlined" startIcon={<ArrowBack/>} onClick={()=>{navigate(-1)}}>Atras</Button>
                    <Box display={"flex"} flexDirection={"column"} gap={2} ml={5}>
                        <Typography variant="h4" fontWeight={700} textAlign={"left"}>Balance de Préstamos</Typography>
                        <Typography variant="body1" color="text.secondary" textAlign={"left"} sx={{mt:-1}}>Balance general de todos los préstamos</Typography>
                    </Box>
                  </Box>

                  {/* Filters Top Bar */}
                  <Paper sx={{ p: 3, mb: 3 }}>
                    {filterContent}
                  </Paper>

                  {/* Metric Cards */}
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    useFlexGap
                    sx={{ mb: 4, flexWrap: "wrap" }}
                  >
                    <MetricCard 
                      label="Total Aprobado" 
                      value={currency(totals.totalApproved)} 
                    />
                    <MetricCard 
                      label="Capital Pagado" 
                      value={currency(totals.totalCapitalPaid)} 
                    />
                    <MetricCard 
                      label="Interes Pagado" 
                      value={currency(totals.totalInterestPaid)} 
                    />
                    <MetricCard 
                      label="Préstamos" 
                      value={totals.activeLoans} 
                    />
                  </Stack>

                  {/* Data Grid */}
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {filteredRows.length === 0 && !isLoadingData ? (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        py: 8,
                        textAlign: 'center'
                      }}>
                        <Typography variant="h6" fontWeight={600} color="text.primary" mb={1}>
                          No hay datos disponibles
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={3}>
                          No se encontraron préstamos para los filtros seleccionados.
                        </Typography>
                        <Button 
                          variant="contained" 
                          onClick={handleGenerate}
                          color="primarybutton"
                        >
                          Aplicar Filtros
                        </Button>
                      </Box>
                    ) : (
                      <DataGrid
                        autoHeight
                        rows={filteredRows}
                        columns={columns}
                        minHeight={800}
                        disableRowSelectionOnClick
                        loading={isLoadingData}
                        pageSizeOptions={[5, 10, 25]}
                        initialState={{
                          pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        sx={{
                          "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
                        }}
                      />
                    )}
                  </Paper>

                  {/* Totals footer */}
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={3}
                    sx={{ mt: 2, color: "text.secondary" }}
                  >
                    <Typography variant="body2">
                      <strong>Total Préstamos Aprobados:</strong> {currency(totals.totalApproved)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Capital Pagado:</strong> {currency(totals.totalCapitalPaid)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Interes Pagado:</strong> {currency(totals.totalInterestPaid)}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Container>
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
